import { returnSubOrderReasonOptions, ReturnSubOrderReason } from "@constant/ReturnOrderReason";
import { returnTypeOptions } from "@constant/ReturnOrderStatus";
import { SourceOrderPrefill } from "@redux/sharing/ReturnProductManagementSlice";
import cityRegionData from "@utils/CityRegionData";

import React from "react";
import { Button, Col, Form, Input, message, Row, Select, Skeleton, Space } from "antd";
import styled from "styled-components";

const Wrapper = styled.div`
  max-width: 720px;

  && .ant-form-item-required.ant-form-item-no-colon::before {
    display: none;
  }
  && .ant-form-item-required.ant-form-item-no-colon::after {
    display: inline-block;
    margin-left: 4px;
    color: #ff4d4f;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
    content: "*";
  }
`;

export interface Step1FormValues {
  receiverName: string;
  receiverPhone: string;
  city?: string;
  region?: string;
  addressDetail: string;
  isReturnToWarehouse: boolean;
  returnReason: string;
  returnReasonOther?: string;
  note: string;
}

const normalizeCityChar = (text: string) => text.replace(/台/g, "臺");

const parseAddress = (address?: string): { city?: string; region?: string; addressDetail: string } => {
  if (!address) return { addressDetail: "" };

  const normalized = normalizeCityChar(address);
  const city = Object.keys(cityRegionData).find((cityName) => normalized.startsWith(cityName));
  if (!city) return { addressDetail: address };

  const restOriginal = address.slice(city.length);
  const restNormalized = normalized.slice(city.length);
  const region = Object.keys(cityRegionData[city]).find((regionName) => restNormalized.startsWith(regionName));
  if (!region) return { city, addressDetail: restOriginal };

  return { city, region, addressDetail: restOriginal.slice(region.length) };
};

type Props = {
  prefill?: SourceOrderPrefill;
  isFetching: boolean;
  initialValues?: Step1FormValues;
  onNextStep: (values: Step1FormValues) => void;
};

export default function ReturnProductAppCreateStep1(props: Props) {
  const { prefill, isFetching, initialValues, onNextStep } = props;
  const [form] = Form.useForm<Step1FormValues>();
  const city = Form.useWatch("city", form);
  const returnReason = Form.useWatch("returnReason", form);

  const cityOptions = Object.keys(cityRegionData).map((cityName) => ({ label: cityName, value: cityName }));
  const regionOptions = city
    ? Object.keys(cityRegionData[city]).map((regionName) => ({ label: regionName, value: regionName }))
    : [];

  const {
    city: parsedCity,
    region: parsedRegion,
    addressDetail: parsedAddressDetail,
  } = parseAddress(prefill?.receiverAddress);
  const formInitialValues: Partial<Step1FormValues> = initialValues || {
    receiverName: prefill?.receiverName,
    receiverPhone: prefill?.receiverPhone,
    city: parsedCity,
    region: parsedRegion,
    addressDetail: parsedAddressDetail,
    isReturnToWarehouse: true,
  };

  const handleFinish = (values: Step1FormValues) => {
    onNextStep(values);
  };

  const handleFinishFailed = () => {
    message.error("請先填寫必填欄位");
  };

  if (isFetching || !prefill) {
    return <Skeleton active />;
  }

  return (
    <Wrapper>
      <Form
        form={form}
        labelCol={{ span: 7 }}
        labelAlign="left"
        colon={false}
        initialValues={formInitialValues}
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
      >
        <Form.Item label="退貨通路/平台">
          <span>{prefill.platform || "-"}</span>
        </Form.Item>
        <Form.Item label="原始訂單單號">
          <span>{prefill.originalPlatformNumber || "-"}</span>
        </Form.Item>
        <Form.Item label="退件人名稱" name="receiverName" rules={[{ required: true, message: "請先填寫必填欄位" }]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item label="退件人電話" name="receiverPhone">
          <Input allowClear />
        </Form.Item>
        <Form.Item label="退件人地址">
          <Row gutter={8}>
            <Col span={7}>
              <Form.Item name="city" noStyle>
                <Select
                  options={cityOptions}
                  placeholder="縣市"
                  allowClear
                  showSearch
                  onChange={() => form.setFieldsValue({ region: undefined })}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="region" noStyle>
                <Select options={regionOptions} placeholder="區域" allowClear showSearch />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="addressDetail" noStyle>
                <Input placeholder="詳細地址" allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item label="退貨類型" name="isReturnToWarehouse">
          <Select options={returnTypeOptions} placeholder="請選擇" />
        </Form.Item>
        <Form.Item label="退貨原因" name="returnReason" rules={[{ required: true, message: "請先填寫必填欄位" }]}>
          <Select options={returnSubOrderReasonOptions} placeholder="請選擇" showSearch allowClear />
        </Form.Item>
        {returnReason === ReturnSubOrderReason.OTHER && (
          <Form.Item
            name="returnReasonOther"
            wrapperCol={{ offset: 7 }}
            rules={[{ required: true, whitespace: true, message: "請先填寫必填欄位" }]}
          >
            <Input allowClear />
          </Form.Item>
        )}
        <Form.Item label="備註" name="note">
          <Input.TextArea allowClear />
        </Form.Item>
        <Row justify="end">
          <Space>
            <Button type="primary" htmlType="submit">
              儲存此分頁
            </Button>
          </Space>
        </Row>
      </Form>
    </Wrapper>
  );
}
